// Date ranges for the Insights filter bar.
//
// PORTED VERBATIM from CGS2022 wwwroot/js/contactsQueryFilters.js (contactQueryFunctions) and
// the setDatePickers()/setTimeFrameTrendDefault() switches in
// Scripts/InsightComponents/insightsHeaderExpanded.vue.
//
// Why verbatim: every insight query sends these dates to the API, and the CGS pages are the
// reference the numbers are checked against. A "cleaner" week or quarter boundary here would
// make the two apps disagree on the same filter, and nobody could tell which one is right.
// Known quirks are kept on purpose and called out inline. Change them in both apps or neither.
//
// Each function returns a locale date STRING (toLocaleDateString), as the CGS originals do.
// Callers wrap the result in new Date(...), again as CGS does.

export const dateFns = {
  getPastDate(daysAgo) {
    const date = new Date()
    return new Date(date.setDate(date.getDate() - daysAgo)).toLocaleDateString()
  },
  getFutureDate(daysFromNow) {
    const date = new Date()
    return new Date(date.setDate(date.getDate() + daysFromNow)).toLocaleDateString()
  },
  // Weeks start on Monday. Quirk kept from CGS: on a Sunday (getDay() === 0) the diff is
  // 6 - 0 = 6, which lands on the Monday before.
  getFirstDayOfWeek(date = null) {
    if (!date) date = new Date()
    const dayOfWeek = date.getDay()
    const firstDayOfWeek = new Date(date)
    const diff = dayOfWeek >= 1 ? dayOfWeek - 1 : 6 - dayOfWeek
    firstDayOfWeek.setDate(date.getDate() - diff)
    firstDayOfWeek.setHours(0, 0, 0, 0)
    return firstDayOfWeek.toLocaleDateString()
  },
  getLastDayOfWeek(date = null) {
    if (!date) date = new Date()
    const dayOfWeek = date.getDay()
    const lastDayOfWeek = new Date(date)
    const diff = (7 + (7 - dayOfWeek)) % 7
    lastDayOfWeek.setDate(date.getDate() + diff)
    lastDayOfWeek.setHours(0, 0, 0, 0)
    return lastDayOfWeek.toLocaleDateString()
  },
  getFirstDayOfLastWeek() {
    const firstDayOfThisWeek = new Date(dateFns.getFirstDayOfWeek())
    const lastDayOfLastWeek = firstDayOfThisWeek.setDate(firstDayOfThisWeek.getDate() - 1)
    return dateFns.getFirstDayOfWeek(new Date(lastDayOfLastWeek))
  },
  getLastDayOfLastWeek() {
    const firstDayOfThisWeek = new Date(dateFns.getFirstDayOfWeek())
    const lastDayOfLastWeek = firstDayOfThisWeek.setDate(firstDayOfThisWeek.getDate() - 1)
    return new Date(lastDayOfLastWeek).toLocaleDateString()
  },
  getLastWeekToDay() {
    const firstDayOfThisWeek = new Date(dateFns.getFirstDayOfWeek()).setHours(0, 0, 0)
    const date = new Date().setHours(0, 0, 0)
    const oneDay = 24 * 60 * 60 * 1000
    const daysDiff = Math.round(Math.abs((firstDayOfThisWeek - date) / oneDay))
    const firstDayOfLastWeek = new Date(dateFns.getFirstDayOfLastWeek())
    return new Date(firstDayOfLastWeek.setDate(firstDayOfLastWeek.getDate() + parseInt(daysDiff))).toLocaleDateString()
  },
  getFirstDayOfThisMonth() {
    const date = new Date()
    return new Date(date.getFullYear(), date.getMonth(), 1).toLocaleDateString()
  },
  getLastDayOfThisMonth() {
    const date = new Date()
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).toLocaleDateString()
  },
  getFirstDayOfLastMonth() {
    const date = new Date()
    const monthIndex = date.getMonth()
    const lastMonthIndex = monthIndex === 0 ? 11 : monthIndex - 1
    const year = date.getFullYear()
    const lastMonthYear = monthIndex === 0 ? year - 1 : year
    return new Date(lastMonthYear, lastMonthIndex, 1).toLocaleDateString()
  },
  getLastDayOfLastMonth() {
    const date = new Date()
    date.setDate(1)
    date.setHours(-1)
    return date.toLocaleDateString()
  },
  getLastMonthToDate() {
    const firstDayOfLastMonth = new Date(dateFns.getFirstDayOfLastMonth())
    const lastDayOfLastMonth = new Date(dateFns.getLastDayOfLastMonth()).setHours(0, 0, 0)
    const lastDayOfLastMonthDate = new Date(lastDayOfLastMonth).getDate()
    const date = new Date().getDate()
    if (date <= lastDayOfLastMonthDate) {
      return new Date(firstDayOfLastMonth.setDate(firstDayOfLastMonth.getDate() + parseInt(date - 1))).toLocaleDateString()
    }
    return new Date(lastDayOfLastMonth).toLocaleDateString()
  },
  getFirstDayOfThisQuarter() {
    const date = new Date()
    const quarter = Math.floor(date.getMonth() / 3)
    return new Date(date.getFullYear(), quarter * 3, 1).toLocaleDateString()
  },
  getLastDayOfThisQuarter() {
    const quarterStart = new Date(dateFns.getFirstDayOfThisQuarter())
    return new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 0).toLocaleDateString()
  },
  getFirstDayOfLastQuarter() {
    const date = new Date()
    const quarter = Math.floor(date.getMonth() / 3)
    return new Date(date.getFullYear(), quarter * 3 - 3, 1).toLocaleDateString()
  },
  getLastDayOfLastQuarter() {
    const quarterStart = new Date(dateFns.getFirstDayOfLastQuarter())
    return new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 0).toLocaleDateString()
  },
  getLastQuarterToDate() {
    const firstDayOfThisQuarter = new Date(dateFns.getFirstDayOfThisQuarter()).setHours(0, 0, 0)
    const date = new Date().setHours(0, 0, 0)
    const oneDay = 24 * 60 * 60 * 1000
    const daysDiff = Math.round(Math.abs((firstDayOfThisQuarter - date) / oneDay))
    const firstDayOfLastQuarter = new Date(dateFns.getFirstDayOfLastQuarter())
    return new Date(firstDayOfLastQuarter.setDate(firstDayOfLastQuarter.getDate() + parseInt(daysDiff))).toLocaleDateString()
  },
  getFirstDayOfThisYear() {
    return new Date(new Date().getFullYear(), 0, 1).toLocaleDateString()
  },
  getLastDayOfThisYear() {
    return new Date(new Date().getFullYear(), 11, 31).toLocaleDateString()
  },
  getFirstDayOfLastYear() {
    return new Date(new Date().getFullYear() - 1, 0, 1).toLocaleDateString()
  },
  getLastDayOfLastYear() {
    return new Date(new Date().getFullYear() - 1, 11, 31).toLocaleDateString()
  },
  getLastYearToDate() {
    const date = new Date()
    if (date.getMonth() + 1 === 2 && date.getDate() === 29) date.setDate(28)
    return new Date(date.setFullYear(date.getFullYear() - 1)).toLocaleDateString()
  }
}

// The Time Frame dropdown, in CGS order.
export const DATE_RANGE_PRESETS = [
  'This Year', 'Last Year To Date', 'This Quarter', 'Last Quarter To Date', 'This Month',
  'Last Month To Date', 'Last Month', 'This Week', 'Last Week To Day', 'Today', 'Yesterday', 'Other'
]

// insightsHeaderExpanded.setDatePickers(). Returns null for 'Other' (the user's own dates
// stand) and for anything unknown.
export function rangeFor(preset) {
  const d = s => new Date(s)
  switch (preset) {
    case 'This Year': return { start: d(dateFns.getFirstDayOfThisYear()), end: d(dateFns.getLastDayOfThisYear()) }
    case 'Last Year To Date': return { start: d(dateFns.getFirstDayOfLastYear()), end: d(dateFns.getLastYearToDate()) }
    case 'This Quarter': return { start: d(dateFns.getFirstDayOfThisQuarter()), end: d(dateFns.getLastDayOfThisQuarter()) }
    case 'Last Quarter To Date': return { start: d(dateFns.getFirstDayOfLastQuarter()), end: d(dateFns.getLastQuarterToDate()) }
    case 'This Month': return { start: d(dateFns.getFirstDayOfThisMonth()), end: d(dateFns.getLastDayOfThisMonth()) }
    case 'Last Month To Date': return { start: d(dateFns.getFirstDayOfLastMonth()), end: d(dateFns.getLastMonthToDate()) }
    case 'Last Month': return { start: d(dateFns.getFirstDayOfLastMonth()), end: d(dateFns.getLastDayOfLastMonth()) }
    case 'This Week': return { start: d(dateFns.getFirstDayOfWeek()), end: d(dateFns.getLastDayOfWeek()) }
    case 'Last Week To Day': return { start: d(dateFns.getFirstDayOfLastWeek()), end: d(dateFns.getLastWeekToDay()) }
    case 'Today': return { start: new Date(), end: new Date() }
    case 'Yesterday': return { start: d(dateFns.getPastDate(1)), end: d(dateFns.getPastDate(1)) }
    default: return null
  }
}

// insightsHeaderExpanded.setTimeFrameTrendDefault(). Picking a Time Frame moves the Trend Time
// Frame to its matching comparison window. Returns undefined when CGS leaves the trend alone
// (Last Year To Date, Last Month, Yesterday and so on).
export function trendPresetFor(preset) {
  return {
    'This Year': 'Last Year To Date',
    'This Quarter': 'Last Quarter To Date',
    'This Month': 'Last Month To Date',
    'This Week': 'Last Week To Day',
    'Today': 'Yesterday',
    'Other': 'Other'
  }[preset]
}

// insightsHeaderExpanded.getDefaultTimeFrame(). A page's timeFrame prop picks its starting
// window: none means This Year compared with Last Year To Date.
export function defaultWindowFor(timeFrame) {
  if (timeFrame === 'today') {
    return {
      range: 'Today', trend: 'Yesterday',
      start: new Date(), end: new Date(),
      trendStart: new Date(dateFns.getPastDate(1)), trendEnd: new Date(dateFns.getPastDate(1))
    }
  }
  if (timeFrame === 'month') {
    return {
      range: 'This Month', trend: 'Last Month To Date',
      start: new Date(dateFns.getFirstDayOfThisMonth()), end: new Date(dateFns.getLastDayOfThisMonth()),
      trendStart: new Date(dateFns.getFirstDayOfLastMonth()), trendEnd: new Date(dateFns.getLastMonthToDate())
    }
  }
  return {
    range: 'This Year', trend: 'Last Year To Date',
    start: new Date(dateFns.getFirstDayOfThisYear()), end: new Date(dateFns.getLastDayOfThisYear()),
    trendStart: new Date(dateFns.getFirstDayOfLastYear()), trendEnd: new Date(dateFns.getLastYearToDate())
  }
}
